import { Document } from 'features/files/types'
import {
  isImageDocument,
  isPdfDocument,
} from 'features/files/utils/DocumentUtils'
import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'

/**
 * Process document entries into Document objects
 */
export const processDocumentEntries = (
  documentEntries: readonly GeckoEntry[]
): ReadonlyArray<Document> => {
  return documentEntries.map((entry) => {
    return {
      entryId: entry.id,
      name: (entry.data.filename as string) || `Unknown file`,
      size: entry.data.size ? parseInt(entry.data.size as string) : undefined,
      type: (entry.data.type as string) || 'unknown',
      uploadDate: entry.data.uploadDate as string,
      fields: (entry.data.fields as Record<string, string>) || {},
    }
  })
}

export const useLinkedDocumentsForEntry = (
  entry: GeckoEntry
): {
  documents: ReadonlyArray<Document>
  isLoading: boolean
  documentEntries: ReadonlyArray<GeckoEntry>
  forceRefresh: () => Promise<void>
} => {
  return useLinkedDocuments(entry.id, entry.type)
}

/**
 * Hook to fetch and process documents for a given entry
 */
export const useLinkedDocuments = (
  entryId: string,
  entryType: string
): {
  documents: ReadonlyArray<Document>
  isLoading: boolean
  documentEntries: ReadonlyArray<GeckoEntry>
  forceRefresh: () => Promise<void>
} => {
  const queryParams = useMemo(
    () => [
      {
        type: RolodexEntryType.REPORT,
        tags: { [entryType]: entryId },
      },
    ],
    [entryType, entryId]
  )

  const {
    entries: documentEntries,
    isLoading: documentsLoading,
    forceRefresh,
  } = useRolodexEntries(queryParams)

  // Process document entries into Document objects
  const documents = useMemo(
    () => processDocumentEntries(documentEntries),
    [documentEntries]
  )

  return useMemo(
    () => ({
      documents,
      isLoading: documentsLoading,
      documentEntries,
      forceRefresh,
    }),
    [documents, documentsLoading, documentEntries, forceRefresh]
  )
}

/**
 * Hook to get signed URLs for document previews
 */
export const useDocumentSignedUrls = (document: Document | null) => {
  const isPdf = useMemo(() => isPdfDocument(document), [document])
  const isImage = useMemo(() => isImageDocument(document), [document])

  const urlParams = useMemo(
    () =>
      document && (isPdf || isImage) && document.name
        ? [{ entry_id: document.entryId, path: document.name }]
        : undefined,
    [document, isPdf, isImage]
  )

  const { urls: signedUrls, isLoading: urlsLoading } =
    useRolodexSignedURLs(urlParams)

  return {
    signedUrl: signedUrls.length > 0 ? signedUrls[0] : undefined,
    isLoading: urlsLoading,
    isPdf,
    isImage,
  }
}
